import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router";

import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { isDefined } from "@/helpers/util";
import { bookableAssetMutations } from "@/queries/bookable-assets/mutations";
import { useBookableAssetsQueries } from "@/queries/bookable-assets/queryOptions";

import { ReservationDetails, type ReservationDetailsProps } from "./ReservationDetails";

export function ReservationDetailsPage(): React.ReactNode {
  const bookableAssetsQueries = useBookableAssetsQueries();
  const deleteReservationMutation = bookableAssetMutations.useDeleteBooking();

  const { aid: assetId, rid: reservationId } = useParams<{ aid: string; rid: string }>();

  const {
    data: reservation,
    isPending: isLoading,
    error: detailsError,
  } = useQuery({
    ...bookableAssetsQueries.bookingDetails({
      assetId: assetId!,
      bookingId: reservationId!,
    }),
    enabled: isDefined(assetId) && isDefined(reservationId),
  });

  const error = !isDefined(assetId) || !isDefined(reservationId) || detailsError;
  if (error) {
    return <ErrorPage error={error} />;
  }

  const loading = isLoading || reservation === undefined;
  if (loading) {
    return <FullSizeLoader withPadding />;
  }

  const detailsProps: ReservationDetailsProps = {
    reservation,
    onDelete: () =>
      deleteReservationMutation.mutateAsync({
        bookingId: reservationId,
        assetId: assetId,
      }),
  };

  return <ReservationDetails {...detailsProps} />;
}
